// @ts-strict-ignore
import { UserModel } from '@user/model/user.model';
import { PropertyValueVO, UserSettingPropertyVO } from '@project/components/article/model/vo/property.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { PropertyValueType } from '@project/constants/property-value-type';
import { UserSettingsDef } from '@user/constants/user-settings-def';
import { lastValueFrom } from 'rxjs';
import { UserDataService } from '../service/user-data.service';
import { Injectable } from '@angular/core';
import { ComplexProjectVO, ProjectVO } from '@project/model/vo/project.vo';

@Injectable({ providedIn: 'root' })
export class UserSettings {
  private readonly ARRAY_ELEMENT_SEPARATOR: string = ',';

  constructor(private userDataService: UserDataService, private userModel: UserModel) {
  }

  public async saveUserSettings(): Promise<void> {
    await lastValueFrom(this.userDataService.saveUserSettingProperties(this.userModel.userProfile));
  }

  public get lastSelectedSmartScriptVersion(): number {
    const value: number = this.getUserSettingNumberValue("lastSelectedSmartScriptVersion");
    return value;
  }

  public get selectedCadFormat(): string {
    const selectedCadFormat: string = this.getUserSettingStringValue("selectedCadFormat");
    return selectedCadFormat;
  }

  private getUserSettingProperty(settingName: UserSettingsDef): UserSettingPropertyVO {
    if (!this.userModel || !this.userModel.userProfile || ArrayUtils.isEmpty(this.userModel.userProfile.userSettingProperties)) {
      return null;
    }
    for (const userSettingProperty of this.userModel.userProfile.userSettingProperties) {
      if (userSettingProperty.name === settingName) {
        return userSettingProperty;
      }
    }
    return null;
  }

  private getUserSettingPropertyValue(settingName: UserSettingsDef): PropertyValueVO {
    const userSettingProperty: UserSettingPropertyVO = this.getUserSettingProperty(settingName);
    return PropertyUtils.getFirstPropertyValue(userSettingProperty);
  }

  public getUserSettingStringValue(settingName: UserSettingsDef, defaultValue?: string): string | undefined {
    const propertyValue: PropertyValueVO = this.getUserSettingPropertyValue(settingName);
    if (!propertyValue) {
      return defaultValue;
    }
    return propertyValue.stringValue;
  }

  public getUserSettingBooleanValue(settingName: UserSettingsDef, defaultValue: boolean = false): boolean {
    const propertyValue: PropertyValueVO = this.getUserSettingPropertyValue(settingName);
    if (!propertyValue) {
      return defaultValue;
    }
    return propertyValue.intValue === 1;
  }

  public getUserSettingNumberValue(settingName: UserSettingsDef, defaultValue: number = NaN): number {
    const propertyValue: PropertyValueVO = this.getUserSettingPropertyValue(settingName);
    if (!propertyValue) {
      return defaultValue;
    }
    return propertyValue.intValue;
  }

  /** Assigns a new value to the given setingName in the users settings
   * @returns whether or not the assignment was successful */
  public setUserSettingValue(settingName: UserSettingsDef, settingValue: any): boolean {
    if (!this.userModel || !this.userModel.userProfile || !this.userModel.userProfile.isRegisteredUserAccount()) {
      return false;
    }
    if (Array.isArray(settingValue)) {
      settingValue = this.fromArray(settingValue);
    }
    let property: UserSettingPropertyVO = this.getUserSettingProperty(settingName);
    if (!property) {
      property = new UserSettingPropertyVO();
      property.name = settingName;
      property.transientProperty = false;
      if (!this.userModel.userProfile.userSettingProperties) {
        this.userModel.userProfile.userSettingProperties = [property];
      } else {
        this.userModel.userProfile.userSettingProperties.push(property);
      }
    }
    let propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValue(property);

    if(propertyValue) {
      return this.populatePropertyValue(propertyValue, settingValue);
    } else {
      return this.createPropertyValue(property, propertyValue, settingValue);
    }
  }

  private createPropertyValue(property: UserSettingPropertyVO, propertyValue: PropertyValueVO, settingValue: any) {
      if (typeof settingValue === 'string') {
        propertyValue = PropertyValueVO.create(PropertyValueType.STRING, String(settingValue));
      } else if (typeof settingValue === 'number') {
        propertyValue = PropertyValueVO.create(PropertyValueType.INT);
        propertyValue.intValue = Number(settingValue);
      } else if (typeof settingValue === 'boolean') {
        propertyValue = PropertyValueVO.create(PropertyValueType.INT);
        propertyValue.intValue = settingValue ? 1 : 0;
      } else {
        return false;
      }

      if (ArrayUtils.isEmpty(property.values)) {
        property.values = [propertyValue];
      } else {
        property.values.push(propertyValue);
      }
    return true;
  }

  private populatePropertyValue(propertyValue: PropertyValueVO, settingValue: any) {
    if (typeof settingValue === 'string') {
      propertyValue.stringValue = settingValue.toString();
      return true;
    }
    if (typeof settingValue === 'number') {
      propertyValue.intValue = Number(settingValue);
      return true;
    }
    if (typeof settingValue === 'boolean') {
      propertyValue.intValue = settingValue ? 1 : 0;
      return true;
    }
    return false;
  }

  private fromArray(value: string[]): string {
    return value.join(this.ARRAY_ELEMENT_SEPARATOR);
  }

  public deleteUserSettingValue(settingName: UserSettingsDef): void {
    if (!settingName || !this.userModel || !this.userModel.userProfile || !this.userModel.userProfile.userSettingProperties) {
      return;
    }
    for (let i = 0; i < this.userModel.userProfile.userSettingProperties.length; i++) {
      const userSettingProperty: UserSettingPropertyVO = this.userModel.userProfile.userSettingProperties[i];
      if (userSettingProperty && userSettingProperty.name === settingName) {
        this.userModel.userProfile.userSettingProperties.splice(i, 1);
        return;
      }
    }
  }

  public get lastSelectedProjectGroupId(): number {
    return this.getUserSettingNumberValue("lastSelectedProjectGroupId");
  }

  public get lastSelectedProjectId(): number {
    return this.getUserSettingNumberValue("lastSelectedProjectId");
  }

  public get lastSelectedArticleProjectGroupId(): number {
    return this.getUserSettingNumberValue('lastSelectedArticleProjectGroupId');
  }

  public get automaticRecursivPlausiExecution(): boolean {
    if(!this.userModel || !this.userModel.userProfile) return false;
    return PropertyUtils.getBooleanPropertyValue( this.userModel.userProfile.userSettingProperties, "validationRecursivPlausiExecution" );
  }

  public get lastAdditionalRequestEmails(): string[] {
    const additionalRequestEmails = this.getUserSettingStringValue('lastAdditionalRequestEmails');

    if (additionalRequestEmails) {
      return this.toArray(additionalRequestEmails);
    }

    return null;
  }

  private toArray(value: string): string[] {
    return value.split(this.ARRAY_ELEMENT_SEPARATOR);
  }

  public get cddSelectedCadFormats(): string[] {
    const cddSelectedCadFormats: string = this.getUserSettingStringValue('cddSelectedCadFormats');
    if (cddSelectedCadFormats) {
      return this.toArray(cddSelectedCadFormats);
    }

    return null;
  }

  public get cddSelectedCaeFormats(): string[] {
    const cddSelectedCaeFormats: string = this.getUserSettingStringValue('cddSelectedCaeFormats');
    if (cddSelectedCaeFormats) {
      return this.toArray(cddSelectedCaeFormats);
    }

    return null;
  }

  public get lastSelectedSymbolCharCodeInLabelEditor(): number {
    const lastSelectedSymbolCharCode: number = this.getUserSettingNumberValue('lastSelectedSymbolCharCodeInLabelEditor');
    return lastSelectedSymbolCharCode;
  }

  public get cddSelectedOtherOptions(): string[] {
    const cddSelectedOtherOptions: string = this.getUserSettingStringValue('cddSelectedOtherOptions');
    if (cddSelectedOtherOptions) {
      return this.toArray(cddSelectedOtherOptions);
    }
    return null;
  }

  public get lastSelectedPESymbolInLabelEditor(): string {
    const lastSelectedPESymbol: string = this.getUserSettingStringValue('lastSelectedPESymbolInLabelEditor');
    return lastSelectedPESymbol;
  }

  public get lastSelectedNSymbolInLabelEditor(): string {
    const lastSelectedNSymbol: string = this.getUserSettingStringValue("lastSelectedNSymbolInLabelEditor");
    return lastSelectedNSymbol;
  }
  public get historySortAttribute(): string {
    const historySortAttribute: string = this.getUserSettingStringValue("historySortAttribute");
    return historySortAttribute;
  }

  // boolean for sort order asc
  public get historySortOrder(): boolean {
    const historySortOrder: number = this.getUserSettingNumberValue("historySortOrder");
    if (historySortOrder !== null) {
      return Boolean(historySortOrder);
    }
    return false;
  }

  public saveLastSelectedProjectFromComplexProjectVO(complexProjectVO: ComplexProjectVO) {
    if (complexProjectVO && complexProjectVO.project && complexProjectVO.project.projectGroup) {
      this.saveLastSelectedProject(complexProjectVO.project.projectGroup.id, complexProjectVO.project.id, complexProjectVO.isArticleProject);
    }
  }

  public saveLastSelectedProjectFromProjectVO(projectVO: ProjectVO, isArticleProject: boolean) {
    if (projectVO) {
      this.saveLastSelectedProject(projectVO.projectGroup.id, projectVO.id, isArticleProject);
    }
  }

  private saveLastSelectedProject(projectGroupId: number, projectId: number, isArticleProject: boolean = false): void {
    if (ArrayUtils.isEmpty(this.userModel.userProfile.userSettingProperties)) {
      return;
    }

    if (isArticleProject) {
      this.setUserSettingValue("lastSelectedArticleProjectGroupId", projectGroupId);
    } else {
      this.setUserSettingValue("lastSelectedProjectGroupId", projectGroupId);
      this.setUserSettingValue("lastSelectedProjectId", projectId);
    }
    this.saveUserSettings();
  }
}
